import { useUIv2 } from '@/hooks/useUIv2'
import SettingsV2 from './SettingsV2'
import { trackEvent } from '@/utils/analytics'
import { isAramcoEnv } from '@/config/environment'
import { useCallback, useEffect, useState } from 'react'
import { apiService } from '@/service/api'
import PageLayout from '@/components/layout/PageLayout'
import { Input } from '@/components/ui/input'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useAppStore } from '@/store'
import { useTranslation } from '@/i18n'
import ProfilePhotoField from '@/components/ProfilePhotoField'
import Team from '@/routes/Settings/Team'
import AgentManagement from '@/routes/Settings/AgentManagement'
import AgentNicknames from '@/routes/Settings/AgentNicknames'
import AppearanceSettings from '@/routes/Settings/AppearanceSettings'
import PlatformAgentProvisioning from '@/routes/Settings/PlatformAgentProvisioning'
import OnboardingQuestions from '@/routes/Settings/OnboardingQuestions'
import AuditLog from '@/routes/Settings/AuditLog'
import KnowledgeGraph from '@/routes/Settings/KnowledgeGraph'
import UserInteractionData from '@/components/UserInteractionData'
import EgressPolicyControl from '@/routes/SettingsPage/EgressPolicyControl'
import {
  User,
  Users,
  Sparkles,
  BarChart3,
  HelpCircle,
  FileText,
  Settings as SettingsIcon,
  Search,
  Activity,
  Bot,
  Shield,
  ShieldCheck,
  PencilLine,
  Palette,
  Network,
} from 'lucide-react'

const SECTIONS = [
  'profile',
  'team',
  'agents',
  'agent-nicknames',
  'appearance',
  'features',
  'security',
  'usage',
  'onboarding',
  'logs',
  'advanced',
  'queues',
  'platform-agents',
  'knowledge-graph',
] as const

type Section = (typeof SECTIONS)[number]

function isValidSection(value: string | null): value is Section {
  return value !== null && SECTIONS.includes(value as Section)
}

const ADMIN_ONLY_SECTIONS: ReadonlySet<Section> = new Set([
  'appearance',
  'features',
  // Deliberately absent from INTERNAL_ONLY_SECTIONS below: the egress policy is
  // configured by the CUSTOMER tenant's org-admin, which is what the server-side
  // `role === 'admin'` gate encodes. See the same split in SettingsV2.
  'security',
  'onboarding',
  'logs',
  'queues',
  'platform-agents',
  'advanced',
  'knowledge-graph',
])

const INTERNAL_ONLY_SECTIONS: ReadonlySet<Section> = new Set([
  'features',
  'queues',
  'platform-agents',
  'knowledge-graph',
])

export default function SettingsPage() {
  const isV2 = useUIv2()
  if (isV2) return <SettingsV2 />
  return <SettingsPageLegacy />
}

function SettingsPageLegacy() {
  const { t } = useTranslation()
  const { user } = useAppStore()
  const isAdmin = user?.role === 'admin'
  const isInternalUser =
    (user?.email?.endsWith('@intuigence.ai') ||
      user?.email?.endsWith('@intuigence.com')) ??
    false
  const storedTab = sessionStorage.getItem('settings-tab')

  const resolveTab = (value: string | null): Section | null => {
    if (
      isValidSection(value) &&
      (!ADMIN_ONLY_SECTIONS.has(value) || isAdmin) &&
      (!INTERNAL_ONLY_SECTIONS.has(value) || isInternalUser)
    ) {
      return value
    }
    return null
  }

  const [activeSection, setActiveSectionState] = useState<Section>(
    () => resolveTab(storedTab) ?? 'profile',
  )

  const setActiveSection = useCallback((section: Section) => {
    sessionStorage.setItem('settings-tab', section)
    setActiveSectionState(section)
  }, [])

  const [searchQuery, setSearchQuery] = useState('')

  // Counter chip on the Knowledge Graph sidebar item — the count of
  // failed + stuck uploads in this tenant. Fetched once when the
  // Settings page mounts, but only for users who can actually see the
  // section. Failures swallow silently so a flaky backend doesn't
  // break Settings; the chip just doesn't appear.
  //
  // Visibility derives from the same `ADMIN_ONLY_SECTIONS` /
  // `INTERNAL_ONLY_SECTIONS` sets that gate the sidebar item itself
  // (greptile P2 #11686 follow-up). Hard-coding `isAdmin &&
  // isInternalUser` here would silently drift if the section's
  // visibility changes — derive from the sets instead.
  const [kgNeedsAttention, setKgNeedsAttention] = useState<number | null>(null)
  const canSeeKg =
    (!ADMIN_ONLY_SECTIONS.has('knowledge-graph') || isAdmin) &&
    (!INTERNAL_ONLY_SECTIONS.has('knowledge-graph') || isInternalUser)
  useEffect(() => {
    if (!canSeeKg) return
    let cancelled = false
    apiService
      .fetchOrError(apiService.getUrl('v1/knowledge-graph/uploads/counts'))
      .then((r) => r.json())
      .then((data: { needs_attention?: number }) => {
        if (!cancelled && typeof data?.needs_attention === 'number') {
          setKgNeedsAttention(data.needs_attention)
        }
      })
      .catch(() => {
        // Silent — see comment above. Settings must mount regardless.
      })
    return () => {
      cancelled = true
    }
  }, [canSeeKg])

  const sidebarItems = [
    {
      id: 'profile' as Section,
      label: t('common:settings.tabs.profile'),
      icon: User,
      adminOnly: false,
    },
    {
      id: 'team' as Section,
      label: t('common:settings.tabs.team'),
      icon: Users,
      adminOnly: false,
    },
    {
      id: 'agents' as Section,
      label: t('common:settings.tabs.agents'),
      icon: Bot,
      adminOnly: false,
    },
    {
      id: 'agent-nicknames' as Section,
      label: t('common:settings.tabs.agent_nicknames'),
      icon: PencilLine,
      adminOnly: false,
    },
    {
      id: 'appearance' as Section,
      label: t('common:settings.tabs.appearance'),
      icon: Palette,
      adminOnly: true, // Set to false to make dark mode available to all users
    },
    {
      id: 'features' as Section,
      label: t('common:settings.tabs.features'),
      icon: Sparkles,
      adminOnly: true,
      internalOnly: true,
    },
    {
      id: 'security' as Section,
      label: t('common:settings.tabs.security', 'Security'),
      icon: ShieldCheck,
      adminOnly: true,
    },
    {
      id: 'usage' as Section,
      label: t('common:settings.tabs.usage_limits'),
      icon: BarChart3,
      adminOnly: false,
      comingSoon: true,
    },
    {
      id: 'onboarding' as Section,
      label: t('common:settings.tabs.onboarding_questions'),
      icon: HelpCircle,
      adminOnly: true,
      comingSoon: true,
    },
    {
      id: 'logs' as Section,
      label: t('common:settings.tabs.logs'),
      icon: FileText,
      adminOnly: true,
    },
    {
      id: 'queues' as Section,
      label: t('common:settings.tabs.queue_monitor'),
      icon: Activity,
      adminOnly: true,
      internalOnly: true,
    },
    {
      id: 'platform-agents' as Section,
      label: t('common:settings.tabs.platform_agents'),
      icon: Shield,
      adminOnly: true,
      internalOnly: true,
    },
    {
      id: 'knowledge-graph' as Section,
      label: t('common:settings.tabs.knowledge_graph', 'Knowledge Graph'),
      icon: Network,
      adminOnly: true,
      internalOnly: true,
    },
    {
      id: 'advanced' as Section,
      label: t('common:settings.tabs.advanced'),
      icon: SettingsIcon,
      adminOnly: true,
      comingSoon: true,
    },
  ]

  const visibleItems = sidebarItems.filter(
    (item) =>
      (!item.adminOnly || isAdmin) && (!item.internalOnly || isInternalUser)
  )

  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text">
                {t('common:settings.tabs.profile')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-1">
                {t('common:settings.profile_description')}
              </p>
            </div>

            <div className="flex flex-col gap-6 p-6 bg-white dark:bg-dark-card rounded-lg border border-gray-200 dark:border-dark-border">
              {/* FS-693: same control as SettingsV2. Both surfaces render this
                  card and which one a user sees depends on VITE_UIV2, so wiring
                  only one would leave half of them without the button. */}
              <ProfilePhotoField />
              <div className="flex flex-col">
                <h4 className="text-xl font-semibold">{user?.name}</h4>
                <span className="text-sm text-gray-500 dark:text-dark-text-muted">{user?.email}</span>
              </div>
            </div>

            <div className="bg-white dark:bg-dark-card p-6 rounded-lg border border-gray-200 dark:border-dark-border">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('common:settings.organization_status')}
                </label>
                <div className="text-lg text-gray-900 dark:text-dark-text">
                  {t('common:settings.active')}
                </div>
              </div>
            </div>
          </div>
        )

      case 'team':
        return (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text">
                {t('common:settings.tabs.team')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-1">
                {t('common:settings.team_description')}
              </p>
            </div>
            <Team />
          </div>
        )

      case 'agents':
        return (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text">
                {t('common:settings.tabs.agents')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-1">
                {t('common:settings.agents_description')}
              </p>
            </div>
            <AgentManagement />
          </div>
        )

      case 'agent-nicknames':
        return (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text">
                {t('common:settings.tabs.agent_nicknames')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-1">
                {t('common:settings.agent_nicknames_description')}
              </p>
            </div>
            <AgentNicknames />
          </div>
        )

      case 'appearance':
        return (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text">
                {t('common:settings.tabs.appearance')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-1">
                {t('common:settings.appearance_description')}
              </p>
            </div>
            <AppearanceSettings />
          </div>
        )

      case 'features':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text">
                {t('common:settings.tabs.features')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-1">
                {t('common:settings.features_description')}
              </p>
            </div>
            <div className="space-y-6">
              <UserInteractionData />
            </div>
          </div>
        )

      case 'usage':
        return (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text">
                {t('common:settings.tabs.usage_limits')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-1">
                {t('common:settings.usage_limits_description')}
              </p>
            </div>
            <div className="p-8 bg-gray-50 dark:bg-dark-muted rounded-lg border border-gray-200 dark:border-dark-border text-center">
              <BarChart3 className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500 mb-3" />
              {!isAramcoEnv && (
                <p className="text-gray-600 dark:text-gray-400">
                  {t('common:settings.usage_coming_soon')}
                </p>
              )}
            </div>
          </div>
        )

      case 'onboarding':
        return (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text">
                {t('common:settings.tabs.onboarding_questions')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-1">
                {t('common:settings.onboarding_description')}
              </p>
            </div>
            <OnboardingQuestions />
          </div>
        )

      case 'logs':
        return (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text">
                {t('common:settings.tabs.logs')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-1">
                {t('common:settings.logs_description')}
              </p>
            </div>
            <AuditLog />
          </div>
        )

      case 'queues':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text">
                {t('common:settings.tabs.queue_monitor')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-1">
                {t('common:settings.queue_monitor_description')}
              </p>
            </div>
            <div
              className="bg-white dark:bg-dark-card rounded-lg border border-gray-200 dark:border-dark-border overflow-hidden"
              style={{ height: 'calc(100vh - 220px)' }}
            >
              <iframe
                src="/v2/api/admin/queues"
                className="w-full h-full border-0"
                title="Queue Monitor"
              />
            </div>
          </div>
        )

      case 'platform-agents':
        return (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text">
                {t('common:settings.tabs.platform_agents')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-1">
                {t('common:settings.platform_agents_description')}
              </p>
            </div>
            <PlatformAgentProvisioning />
          </div>
        )

      case 'security':
        return (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text">
                {t('common:settings.tabs.security', 'Security')}
              </h2>
            </div>
            <EgressPolicyControl />
          </div>
        )

      case 'knowledge-graph':
        // Section component renders its own header — keeps the
        // icon-led layout consistent with the in-page Network badge
        // and avoids a stacked-h2 redundancy that the other sections
        // get away with because they have no in-component title.
        return <KnowledgeGraph />

      case 'advanced':
        return (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text">
                {t('common:settings.tabs.advanced')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-1">
                {t('common:settings.advanced_description')}
              </p>
            </div>
            <div className="p-8 bg-gray-50 dark:bg-dark-muted rounded-lg border border-gray-200 dark:border-dark-border text-center">
              <SettingsIcon className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500 mb-3" />
              {!isAramcoEnv && (
                <p className="text-gray-600 dark:text-gray-400">
                  {t('common:settings.advanced_coming_soon')}
                </p>
              )}
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <TooltipProvider>
      <PageLayout fullScreenContent>
        <div className="flex gap-0 h-full">
          {/* Left Sidebar - 15% of screen width max */}
          <aside className="w-[230px] flex-shrink-0 border-r border-gray-200 dark:border-dark-border px-4 py-6 flex flex-col">
            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute start-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                <Input
                  type="text"
                  placeholder={String(t('common:settings.search'))}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="ps-9"
                />
              </div>
            </div>

            {/* Navigation items */}
            <nav className="flex-1 space-y-1 overflow-y-auto">
              {visibleItems.map((item) => {
                const Icon = item.icon
                const isActive = activeSection === item.id

                const menuButton = (
                  <button
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${item.comingSoon
                        ? 'opacity-50 cursor-not-allowed text-gray-700 dark:text-gray-300'
                        : isActive
                          ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-muted'
                      }`}
                    onClick={() => {
                      if (item.comingSoon) return
                      trackEvent('settings-tab_viewed', { tab_name: item.id })
                      setActiveSection(item.id)
                    }}
                    disabled={item.comingSoon}
                    type="button"
                  >
                    <Icon className="h-4 w-4" />
                    <span className="flex-1 text-start">{item.label}</span>
                    {/* Counter chip — wired today only for the
                        knowledge-graph item; the pattern can be reused
                        for other ops surfaces (queue depth, audit alerts)
                        as they need it. Keep hidden when count is 0 to
                        avoid visual noise on healthy tenants. */}
                    {item.id === 'knowledge-graph' &&
                      kgNeedsAttention !== null &&
                      kgNeedsAttention > 0 && (
                        <span
                          className="ml-auto inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900/30 dark:text-red-300"
                          data-testid="kg-sidebar-chip"
                        >
                          {kgNeedsAttention}
                        </span>
                      )}
                  </button>
                )

                if (item.comingSoon && !isAramcoEnv) {
                  return (
                    <Tooltip key={item.id}>
                      <TooltipTrigger asChild>
                        <span className="block">{menuButton}</span>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <p>{t('common:general.coming_soon')}</p>
                      </TooltipContent>
                    </Tooltip>
                  )
                }

                return <div key={item.id}>{menuButton}</div>
              })}
            </nav>
          </aside>

          {/* Right Content Area */}
          <div className="flex-1 min-h-0 overflow-y-auto px-8 py-6">
            {renderContent()}
          </div>
        </div>
      </PageLayout>
    </TooltipProvider>
  )
}
